import { useCallback } from 'react';
import { useCountryCodeValue, useIpValue } from './system/useCountryCode';
import { useOrgConfigStateValue } from './useOrgConfig';
import { logger } from 'services/logger';
import {
  getProxyOriginSrc,
  isTiktokProxyEnabled,
  isTiktokProxySrc,
  resolveTiktokProxySrc,
} from 'lib/utils/tiktokProxy';
import { isURL } from 'utils/is';
import wait from 'utils/wait';

/**
 * TikTok CDN 是否可直连
 *
 * 探测完成前默认按「不可直连」处理，避免首屏资源先走一遍无法访问的裸地址。
 */
export const proxyRef = { current: false };

Promise.race([
  fetch('https://v16m-default.tiktokcdn.com'),
  wait(3000).then(() => Promise.reject(new Error('timeout'))),
])
  .then(() => {
    proxyRef.current = true;
  })
  .catch(() => {
    proxyRef.current = false;
  });

function useProxySrc() {
  const countryCode = useCountryCodeValue();

  const ip = useIpValue();

  const { tiktokImageProxyOriginList } = useOrgConfigStateValue();

  const getProxyEnable = useCallback(
    (src: string) =>
      isTiktokProxyEnabled(src, {
        directAccessible: proxyRef.current,
        countryCode,
        originList: tiktokImageProxyOriginList,
      }),
    [countryCode, tiktokImageProxyOriginList],
  );

  const errorLog = useCallback(
    (src: string) => {
      const originSrc = isTiktokProxySrc(src) ? getProxyOriginSrc(src) : src;
      logger.info(
        `[loadImageError] ${JSON.stringify({
          ip,
          countryCode,
          src,
          originSrc,
        })}`,
      );
    },
    [ip, countryCode],
  );

  const getOriginSrc = useCallback(
    (src: string) => {
      const [, , partial] = src.split('http');
      if (!partial) {
        return src;
      }
      const originSrc = `http${partial}`;
      if (!isURL(originSrc)) {
        return src;
      }
      if (getProxyEnable(originSrc)) {
        return src;
      }
      return originSrc;
    },
    [getProxyEnable],
  );

  /**
   * 按代理规则解析资源的最终地址
   *
   * 展示、复制、下载、复制链接都应使用它，保证「看到什么就复制/保存什么」。
   * - 需要代理：返回代理地址
   * - 不需要代理：返回原始地址
   * - 传入的已经是代理地址时不会重复包装
   */
  const resolveSrc = useCallback(
    (src: string) =>
      resolveTiktokProxySrc(src, {
        directAccessible: proxyRef.current,
        countryCode,
        originList: tiktokImageProxyOriginList,
      }),
    [countryCode, tiktokImageProxyOriginList],
  );

  return { getProxyEnable, errorLog, getOriginSrc, resolveSrc };
}

export default useProxySrc;
